import { useEventInfo } from '@shared/utils/eventInfo';
import { useBestLocationAnchorQuery } from '@graphql/generated';
import { useEffect } from 'react';
import { withWindow } from '@shared/utils/withWindow';

export const useBestAnchorLocation = ({
  onCompleted,
  skip
}: {
  onCompleted: (data: { latitude: string; longitude: string; friendlyName: string } | null) => void;
  skip: boolean;
}) => {
  const { eventInfo, eventHandle, loading: eventInfoLoading } = useEventInfo();
  const { data, loading, error } = useBestLocationAnchorQuery({
    variables: {
      name: eventHandle || ''
    },
    onCompleted: data => {
      onCompleted(data?.eventByName?.bestLocationAnchor || null);
    },
    batchMode: 'off',
    skip: skip || eventInfoLoading
  });

  useEffect(() => {
    // if we don't have bestLocationAnchor, we need to geocode the name in order to show results.
    // This edge case happens when the user set location in an “older” way where we dont save the lat/long (just the name)
    if (data?.eventByName && !data.eventByName.bestLocationAnchor) {
      const geocoder = withWindow(() => new window.google.maps.Geocoder(), undefined);
      if (!geocoder) return;

      geocoder.geocode({ address: eventInfo?.locationInfo?.name || '' }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK) {
          const detailedPlace = results?.[0];
          if (!detailedPlace) return;

          const lat = detailedPlace.geometry.location.lat();
          const lng = detailedPlace.geometry.location.lng();

          onCompleted({
            latitude: lat.toString(),
            longitude: lng.toString(),
            friendlyName: eventInfo?.locationInfo?.name || ''
          });
        }
      });
    }
  }, [data, eventInfo?.locationInfo?.name, onCompleted]);

  return { bestAnchorLocation: data?.eventByName?.bestLocationAnchor, loading, error };
};
